import type { RootOptions } from "react-dom/client";

import * as Sentry from "@sentry/react";
import { supabaseIntegration } from "@supabase/sentry-js-integration";
import { SupabaseClient } from "@supabase/supabase-js";

import { getEnv } from "@/hooks/useEnv";
import { router } from "@/providers/routerConfig";
import type { UserProfile, Workspace } from "@/supabase.types";
import { userName } from "@/utils/format";

(() => {
  if (getEnv("VITE_SENTRY_DSN")) {
    Sentry.init({
      // ⭐ base config
      dsn: getEnv("VITE_SENTRY_DSN"),
      release: getEnv("VITE_RELEASE_TAG"),
      environment: getEnv("VITE_ENV"),
      debug: false,
      // ! ignores the typical error originating from outdated chunks, see: https://github.com/vitejs/vite/issues/11804
      ignoreErrors: [/dynamically imported module/, /Failed to fetch/],
      sendDefaultPii: true,
      // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
      tracesSampleRate: 1.0,
      beforeSend(event, hint) {
        // ! ignores the typical error originating from outdated chunks, see: https://github.com/vitejs/vite/issues/11804
        if (event.level === "error" && hint.originalException && hint.originalException instanceof Error) {
          if (
            hint.originalException.message.includes("dynamically imported module") ||
            hint.originalException.message.includes("Failed to fetch")
          ) {
            // event is dropped
            return null;
          }
        }
        return event;
      },
      integrations: [
        Sentry.browserSessionIntegration(),
        supabaseIntegration(SupabaseClient, Sentry, {
          tracing: true,
          breadcrumbs: true,
          errors: true,
        }),

        Sentry.tanstackRouterBrowserTracingIntegration(router, {
          shouldCreateSpanForRequest: (url) => {
            return !url.startsWith(`${getEnv("VITE_SUPABASE_URL")}/rest`);
          },
        }),
        // Note: we don't want and need the replay feature, since it was unstable last time tried
      ],
    });
  }
})();

export const reactRootErrorOptions: RootOptions = {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
};

export const handleErrorWithSentry = (error?: Error) => {
  if (Sentry.isInitialized()) {
    Sentry.captureException(error);
  }
};

export const setSentryUser = (profile?: UserProfile | null) => {
  if (Sentry.isInitialized() && profile) {
    Sentry.setUser({
      id: profile.id,
      email: profile.email,
      username: userName(profile),
    });
  }
};
export const clearSentryUser = () => {
  if (Sentry.isInitialized()) {
    Sentry.setUser(null);
  }
};

export const setSentryWorkspace = (workspace?: Workspace | null) => {
  if (Sentry.isInitialized() && workspace) {
    Sentry.setTag("workspace_id", workspace.id);
  }
};
